// MapVisibilityMenu — shared "Eye" dropdown for map visibility toggles.
//
// b99.10.41 (2026-05-21) — Extracted from EditorPage so that every map on
// the site (Editor, Trip Overview, RouteMapViewer, GoRouteDetail) can
// share the same UI for toggling marker/label/arrow visibility.
//
// Props:
//  - items: Array<{
//      key: string                 (unique key)
//      label: string               (visible label)
//      checked: boolean            (current state)
//      onCheckedChange: (v) => void
//      testId?: string             (optional data-testid)
//    }>
//    Items can be separated visually by inserting { key: "sep-X", separator: true }.
//  - disabled?: boolean            (disables the trigger)
//  - testIdPrefix?: string         (defaults to "map-visibility")
//  - portalContainer?: Element     (forwarded to DropdownMenuContent, required
//                                   when the trigger lives inside a fullscreen
//                                   element so the menu portals into the same
//                                   fullscreen container)
//  - title?: string                (button title; defaults to "Visibilidade no mapa")
//  - variant?: "icon" | "button"   (defaults to "icon"; "icon" matches the
//                                   inline map toolbars, "button" matches the
//                                   floating cluster on RouteMapViewer)
//
// The trigger always shows the same `Eye` icon — it does NOT mutate to
// reflect any single toggle's state (which would be misleading when 3+
// toggles share the menu).
import React from "react";
import { useTranslation } from "react-i18next";
import { Eye } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function MapVisibilityMenu({
  items,
  disabled = false,
  testIdPrefix = "map-visibility",
  portalContainer,
  title,
  variant = "icon",
  disabledTitle,
}) {
  const { t } = useTranslation("common");
  const resolvedTitle = title || t("map.visibility.title", "Visibilidade no mapa");
  if (!Array.isArray(items) || items.length === 0) return null;

  const triggerClass =
    variant === "button"
      ? "inline-flex items-center justify-center h-8 w-8 rounded-md border border-input bg-background/90 backdrop-blur-sm hover:bg-muted text-foreground focus:outline-none focus-visible:outline-none focus-visible:ring-0 disabled:opacity-40 disabled:cursor-not-allowed"
      : "hover:bg-muted rounded-lg p-2 text-foreground focus:outline-none focus-visible:outline-none focus-visible:ring-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={triggerClass}
          data-testid={`${testIdPrefix}-trigger`}
          aria-label={resolvedTitle}
          title={disabled && disabledTitle ? disabledTitle : resolvedTitle}
        >
          <Eye className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56 border-2 border-zinc-600 shadow-2xl shadow-black/50 z-[10000]"
        data-testid={`${testIdPrefix}-menu`}
        portalContainer={portalContainer}
      >
        <DropdownMenuLabel className="text-xs">{t("map.visibility.show_on_map", "Mostrar no mapa")}</DropdownMenuLabel>
        {(() => {
          // b99.10.56 (2026-05-21) — "Nada" virtual item: checked iff
          // every togglable item is currently unchecked. Clicking it
          // fires `onCheckedChange(false)` on each checked item so the
          // parent state updates uniformly. Clicking any other item
          // re-checks it as normal — the "Nada" entry auto-uncomputes.
          const toggleItems = items.filter((i) => !i.separator);
          const allUnchecked = toggleItems.length > 0 && toggleItems.every((i) => !i.checked);
          const allChecked = toggleItems.length > 0 && toggleItems.every((i) => i.checked);
          const handleNada = (v) => {
            // Only act when transitioning "off → on" (user is asking
            // to clear). If the user clicks again while it's already
            // checked (everything off), this is a no-op.
            if (v !== true) return;
            for (const it of toggleItems) {
              if (it.checked) it.onCheckedChange(false);
            }
          };
          const handleTudo = (v) => {
            // Mirror of "Nada": only act off → on, checking every item.
            if (v !== true) return;
            for (const it of toggleItems) {
              if (!it.checked) it.onCheckedChange(true);
            }
          };
          return (
            <>
              <DropdownMenuCheckboxItem
                checked={allChecked}
                onCheckedChange={handleTudo}
                data-testid={`${testIdPrefix}-tudo`}
              >
                {t("map.visibility.all", "Tudo")}
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={allUnchecked}
                onCheckedChange={handleNada}
                data-testid={`${testIdPrefix}-nada`}
              >
                {t("map.visibility.none", "Nada")}
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              {items.map((item) =>
                item.separator ? (
                  <DropdownMenuSeparator key={item.key} />
                ) : (
                  <DropdownMenuCheckboxItem
                    key={item.key}
                    checked={item.checked}
                    onCheckedChange={item.onCheckedChange}
                    data-testid={item.testId || `${testIdPrefix}-${item.key}`}
                  >
                    {item.label}
                  </DropdownMenuCheckboxItem>
                )
              )}
            </>
          );
        })()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
